/* The kit's mission tag — "9 Days" in the design, and whatever is actually left
   in the app.

   One unit only, always the largest that still reads as more than one of
   itself, because the tag is 36px wide in the kit and a second unit does not
   fit. Truncating rather than rounding is deliberate: a mission with 23 hours
   left says "23h", not "1 Day", because rounding a deadline up is the one
   direction that can mislead. */

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export type Countdown = { label: string; isUrgent: boolean };

/* Under a day is the point where the tag changes colour in the kit. */
const URGENT_BELOW_MS = DAY;

export const countdownTag = (deadlineMs: number, nowMs: number): Countdown | null => {
  const remaining = deadlineMs - nowMs;

  /* Expired is not "0m left" — the mission is simply gone, and the widget drops
     the tag rather than showing a dead clock. */
  if (remaining <= 0) return null;

  const isUrgent = remaining < URGENT_BELOW_MS;

  if (remaining >= DAY) {
    const days = Math.floor(remaining / DAY);

    return { label: `${days} ${days === 1 ? "Day" : "Days"}`, isUrgent };
  }

  if (remaining >= HOUR) {
    return { label: `${Math.floor(remaining / HOUR)}h`, isUrgent };
  }

  if (remaining >= MINUTE) {
    return { label: `${Math.floor(remaining / MINUTE)}m`, isUrgent };
  }

  /* Below a minute the number would change faster than the tick that redraws
     it, so it stops counting and says so. */
  return { label: "<1m", isUrgent };
};

/* How often a countdown needs redrawing to stay honest: once a minute while
   minutes are showing, once an hour above that, and never once it is days —
   a "9 Days" tag is wrong for at most a minute at the moment it becomes 8. */
export const countdownTickMs = (deadlineMs: number, nowMs: number) => {
  const remaining = deadlineMs - nowMs;

  if (remaining <= 0) return null;
  if (remaining < HOUR) return MINUTE;
  if (remaining < DAY) return HOUR;

  return null;
};
